import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faPen } from "@fortawesome/free-solid-svg-icons/faPen";
import { faRotateLeft } from "@fortawesome/free-solid-svg-icons/faRotateLeft";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Button } from "../../../../../components/Button";
import { Flex } from "../../../../../components/Flex";
import { Text } from "../../../../../components/Text";
import { WarningModal } from "../../../../../components/WarningModal";
import { ConversationComment } from "../../../../../model/integration/dto/meta/MetaConversationComment";
import { TaskPriority } from "../../../../../model/task/types/TaskPriority.enum";
import { integrationSliceSelectors } from "../../../../../redux/integration/integration.selector";
import { integrationActions } from "../../../../../redux/integration/integration.slice";
import { useAppDispatch, useAppSelector } from "../../../../../redux/store";
import { userSliceSelectors } from "../../../../../redux/user/user.selector";
import { ThemeType } from "../../../../../theme/Theme";
import { Rounded } from "../../../../../theme/Rounded";
import { Spacing } from "../../../../../theme/Spacing";
import { AtiraToast } from "../../../../../utils/AtiraToast";
import { standardDate } from "../../../../../utils/Date";
import { getTaskPriorityColor } from "../../../../../utils/taskUtils";
import { WACommentForm } from "./WACommentForm";
import { WACommentPriorityTag } from "./WACommentPriorityTag";
import { WACommentResolvedChip } from "./WACommentResolvedChip";
import { WAUserAvatar } from "../components/WAUserAvatar";

const Card = styled(Flex)<{ $accent: keyof ThemeType; $resolved: boolean }>`
  flex-direction: column;
  gap: ${Spacing.s};
  padding: ${Spacing.m};
  border: 1px solid ${({ theme }) => theme.darkSub};
  border-inline-start: 3px solid ${({ theme, $accent }) => theme[$accent]};
  border-radius: ${Rounded.lg};
  background-color: ${({ theme, $resolved }) =>
    $resolved ? theme.lightSub : theme.white};
`;

const Body = styled(Text)`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  unicode-bidi: plaintext;
  line-height: 1.4;
`;

const Action = styled(Button)`
  margin: 0;
  padding: ${Spacing.xs} ${Spacing.s};
  gap: ${Spacing.xs};
  background-color: transparent;
  color: ${({ theme }) => theme.darkerSub};
  border: 1px solid ${({ theme }) => theme.darkSub};
  font-size: 0.8rem;

  &:hover {
    background-color: ${({ theme }) => theme.sub};
    color: ${({ theme }) => theme.textColor};
  }
`;

type Props = {
  comment: ConversationComment;
};

export const WACommentCard: React.FC<Props> = ({ comment }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const isOrgMember = useAppSelector(userSliceSelectors.selectIsUserOrgMember);
  const integration = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaIntegration,
  );

  const isAuthor = comment.authorId === userId;
  const canDelete = isAuthor || !isOrgMember;
  const resolved = Boolean(comment.resolvedAt);

  const target = {
    userId,
    integrationId: integration!._id,
    conversationId: comment.conversationId,
    commentId: comment._id,
  };

  const onEdit = async (body: string, priority: TaskPriority) => {
    try {
      setSaving(true);

      await dispatch(
        integrationActions.updateWhatsappMetaComment({
          ...target,
          body,
          priority,
        }),
      ).unwrap();

      AtiraToast.success(t("integration.whatsapp.meta.comments.updated"));
      setEditing(false);

      return true;
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);

      return false;
    } finally {
      setSaving(false);
    }
  };

  const onToggleResolved = async () => {
    try {
      setResolving(true);

      await dispatch(
        integrationActions.updateWhatsappMetaComment({
          ...target,
          resolved: !resolved,
        }),
      ).unwrap();
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setResolving(false);
    }
  };

  const onDelete = async () => {
    try {
      setDeleting(true);

      await dispatch(
        integrationActions.deleteWhatsappMetaComment(target),
      ).unwrap();
      await dispatch(
        integrationActions.getWhatsappMetaComments({
          conversationId: comment.conversationId,
          integrationId: integration?._id!,
          userId,
        }),
      ).unwrap();
      AtiraToast.success(t("integration.whatsapp.meta.comments.deleted"));
      setConfirmDelete(false);
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card
      $accent={getTaskPriorityColor(comment.priority) as keyof ThemeType}
      $resolved={resolved}
    >
      <Flex alignItems="center" gap="s" flexWrap="wrap">
        <WAUserAvatar user={comment.author} size="1.75rem" />

        <Flex flexDirection="column">
          <Text fontSize="s" fontWeight="bold">
            {comment.author?.name ||
              t("integration.whatsapp.meta.comments.unknownAuthor")}
          </Text>
          <Text fontSize="xs" color="darkerSub">
            {standardDate<string>(comment.createdAt, undefined, true)}
            {comment.editedAt
              ? ` · ${t("integration.whatsapp.meta.comments.edited")}`
              : ""}
          </Text>
        </Flex>

        <Flex marginInlineStart="auto" gap="xs" alignItems="center">
          {resolved ? <WACommentResolvedChip comment={comment} /> : null}
          <WACommentPriorityTag priority={comment.priority} />
        </Flex>
      </Flex>

      {editing ? (
        <WACommentForm
          initialBody={comment.body}
          initialPriority={comment.priority}
          submitTitle={t("common.save")}
          loading={saving}
          onSubmit={onEdit}
          onCancel={() => setEditing(false)}
          autoFocus
        />
      ) : (
        <Body fontSize="m">{comment.body}</Body>
      )}

      {!editing ? (
        <Flex gap="s" flexWrap="wrap">
          <Action
            icon={resolved ? faRotateLeft : faCheck}
            onClick={onToggleResolved}
            loading={resolving}
            disabled={resolving}
          >
            {resolved
              ? t("integration.whatsapp.meta.comments.reopen")
              : t("integration.whatsapp.meta.comments.resolve")}
          </Action>

          {isAuthor ? (
            <Action icon={faPen} onClick={() => setEditing(true)}>
              {t("common.edit")}
            </Action>
          ) : null}

          {canDelete ? (
            <Action icon={faTrash} onClick={() => setConfirmDelete(true)}>
              {t("common.delete")}
            </Action>
          ) : null}
        </Flex>
      ) : null}

      <WarningModal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={onDelete}
        loading={deleting}
        title={t("integration.whatsapp.meta.comments.delete.title")}
        description={t("integration.whatsapp.meta.comments.delete.description")}
        confirmButtonText={t("common.delete")}
      />
    </Card>
  );
};
